import { CHALLENGE_ROSTER } from './challengeBosses.js';
import { isBossLocked } from './acts.js';

/**
 * Every boss in the game paired with the names of the spell cards it can
 * play (BULLET-HELL.md §D1). Built by instantiating each def's patterns and
 * reading their names — the same call the fight itself makes — so the catalog
 * can never drift from what a boss actually does. Nothing here is kept alive:
 * the pattern objects are discarded once their names are read.
 *
 * Includes the two conditional cards a def can own — the phase-2 unlock and
 * the desperation finale — because both are capturable and both are exactly
 * the ones a player would want to see they've never beaten cleanly.
 *
 * `statistics` is optional and applies the act spoiler gate (acts.js
 * isBossLocked): pass a save's statistics and the catalog contains only bosses
 * the player is allowed to be told about, which is what the player-facing
 * screens want. Omit it — as the tests and any whole-game question do — and the
 * catalog is the whole roster.
 *
 * A pattern name shared by two bosses (most of the vocabulary is shared, with
 * per-boss tuning in ATTACK_CONFIG) shows the same lifetime record under both.
 * That is intended: the player learns "Grid Barrage", not "Grid Barrage as
 * played by the Iron Cadence", and one record per name is what makes the list
 * a set of learnable goals rather than 300 near-duplicate rows.
 */
export function patternCatalog(statistics = null) {
  return CHALLENGE_ROSTER.flatMap((def, index) => {
    if (statistics && isBossLocked(def, statistics)) return [];
    const names = [];
    const add = (pattern) => {
      if (pattern?.name && !names.includes(pattern.name)) names.push(pattern.name);
    };
    for (const pattern of def.createPatterns()) add(pattern);
    if (def.createPhase2Pattern) add(def.createPhase2Pattern());
    if (def.createDesperationPattern) add(def.createDesperationPattern());
    // `index` is the CHALLENGE_ROSTER index, i.e. exactly what Boss Challenge
    // and card practice (BULLET-HELL.md §D8) pass as `challengeBossIndex` —
    // the catalog is this roster, in this order, so the two can't drift.
    return [{ index, bossName: def.name, spriteKey: def.spriteKey, patterns: names }];
  });
}

/**
 * Total distinct card names — the denominator for "captured N of". Takes the
 * same optional `statistics` as patternCatalog, so a gated screen counts
 * against the cards it is actually showing rather than against a total that
 * quietly announces how much game is left.
 */
export function totalPatternCount(statistics = null) {
  const all = new Set();
  for (const boss of patternCatalog(statistics)) for (const name of boss.patterns) all.add(name);
  return all.size;
}
